import React from 'react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { CheckCircle2, Mail } from 'lucide-react';

/**
 * Step 3 — Material selection. Specialty products short-circuit to a
 * "Custom Quote" CTA that mail-drafts the product expert.
 *
 * iter114s.63 — Material cards now auto-advance to Step 4 the moment
 * the customer picks one, matching the Step 1 (Product) and Step 2
 * (Format) UX (those have always auto-advanced). The wizard handles
 * setStep(4) via the `onSelect` callback so the parent stays in
 * control of the state machine.
 */
export const Step3Material = ({
  requiresCustomQuote,
  handleCustomQuoteRequest,
  getAvailableMaterials,
  selectedMaterial,
  setSelectedMaterial,
  onSelect,
}) => (
  <div>
    {requiresCustomQuote() ? (
      <div className="text-center py-12">
        <div className="mb-6 animate-fade-in">
          <h3 className="text-3xl font-bold text-[#4A7C59] mb-4">Custom Quote Required</h3>
          <p className="text-gray-600 text-lg mb-2">
            This is a specialty product that requires a custom quote.
          </p>
          <p className="text-gray-600">
            Click below to request a personalized quote from our team.
          </p>
        </div>
        <Button
          onClick={handleCustomQuoteRequest}
          className="bg-gradient-to-r from-[#6FBF73] to-[#4A7C59] hover:from-[#4A7C59] hover:to-[#6FBF73] text-white text-lg px-10 py-7 h-auto shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-1"
        >
          <Mail className="w-6 h-6 mr-2" />
          GET A CUSTOM QUOTE
        </Button>
        <p className="text-sm text-gray-500 mt-4">
          An email will be created to an EG3 Product Expert with your request
        </p>
      </div>
    ) : (
      <>
        <Label className="text-lg font-semibold mb-4 block">Finish/Material</Label>
        <RadioGroup
          value={selectedMaterial}
          onValueChange={(id) => {
            setSelectedMaterial(id);
            onSelect?.(id);
          }}
        >
          <div className="space-y-3">
            {getAvailableMaterials().map((material) => (
              <div
                key={material.id}
                data-testid={`material-card-${material.id}`}
                className={`flex items-center space-x-3 p-5 rounded-xl border-2 cursor-pointer transition-all duration-300 group ${
                  selectedMaterial === material.id
                    ? 'border-[#6FBF73] bg-gradient-to-r from-green-50 to-green-100 shadow-md scale-[1.02] animate-pulse-ring'
                    : 'border-gray-200 hover:border-[#6FBF73] hover:shadow-lg hover:scale-[1.01] hover:bg-green-50/30'
                }`}
                onClick={() => {
                  setSelectedMaterial(material.id);
                  onSelect?.(material.id);
                }}
              >
                <RadioGroupItem value={material.id} id={material.id} className="transition-transform duration-300 group-hover:scale-110" />
                <Label htmlFor={material.id} className="flex-1 cursor-pointer">
                  <span
                    className={`font-semibold transition-colors duration-300 ${
                      selectedMaterial === material.id ? 'text-[#6FBF73]' : 'text-[#4A7C59] group-hover:text-[#6FBF73]'
                    }`}
                  >
                    {material.name}
                  </span>
                </Label>
                <div
                  className={`transition-opacity duration-300 ${
                    selectedMaterial === material.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                  }`}
                >
                  <CheckCircle2 className="w-5 h-5 text-[#6FBF73]" />
                </div>
              </div>
            ))}
          </div>
        </RadioGroup>
      </>
    )}
  </div>
);
